import { ICourseWithResource } from '@/core/interfaces/course-with-resource.interface';
import synchronizationServices from '@/core/services/synchronization.services';
import Vue from 'vue';
import Component from 'vue-class-component';
import { Ref, Watch } from 'vue-property-decorator';
import { APP_EVENT_BUS } from '@/core/enums/app.enum';
import { showCustomTooltip } from '@/utils/wc-data-table-utils';

@Component({
  name: 'synchronization-admin',
  components: {}
})
export default class SynchronizationAdmin extends Vue {

  /* Data */
  public courseList : ICourseWithResource[] = [] ;
  public loading: boolean = false;
  public contentIdSelectedForDownloading: number = 0;
  public message : string = null;
  public errorMessageList : {message:string,error:any}[] = [];

  @Ref('dataTable') readonly dataTable: any;

  /* Computed */
  get courseHeaders() {
    return [
      {
        text: this.$t('tools.synchronization.sectionCourseFileColumnCourse'), 
        value: 'contentTitle', 
        sortable: false, 
        width: '70%'
      },
      {
        text: this.$t('tools.synchronization.sectionCourseFileColumnFiles'),
        value : 'files',
        sortable: false, 
        width: '15%', 
        align: 'center'
      },
      {
        text: this.$t('tools.synchronization.sectionCourseFileColumnSync'),
        value: 'resourceCachedPercent', 
        sortable: false, 
        width: '15%', 
        align: 'center'
      },
    ];
  }

  get contentIdSelectedForDownloadingValue(): number {
    return this.contentIdSelectedForDownloading;
  }

  get courseListValue(): ICourseWithResource[] {
    return this.courseList;
  }

  @Watch('courseListValue', { deep: false, immediate: true })
  private _watchCourseListValueInDeep(newValue: any, oldValue: any): void {
    this._loadDataInDataTable();
  }

  @Watch('contentIdSelectedForDownloadingValue', { deep: false, immediate: true })
  private _watchContentIdSelectedForDownloadingValue(newValue: number, oldValue: number): void {
    if (oldValue) {
      const lastCourse = this.courseListValue.find((c) => c.contentId === oldValue);
      if (lastCourse) {
        this.dataTable?.updateItem(lastCourse);
      }
    }
  }
  
  /* Methods */
  public async onClickButtonSync() {

    if( this.courseList.length == 0 ) {
      this.logger(`${this.$t('tools.synchronization.msgNoCoursesAvailable')}`);
      return;
    }

    this.errorMessageList = [];
    this.loading = true;
    this.contentIdSelectedForDownloading = 0;

    try{

      if(this.courseList.some(item => item.errorMessage)) {
        await this.init();
      }
      
      for( let course of this.courseList ) {
        course = this._checkCourseChanges(course);
        this.contentIdSelectedForDownloading = course.contentId;

        const contentTitle = course.contentTitle;
        this.logger(`${this.$t('tools.synchronization.msgSynchronizationCourseLoading', {contentTitle})}`);

        const errorMessages = await synchronizationServices.downloadFilesFromCourse(course, this.logger);
        this.errorMessageList = this.errorMessageList.concat( errorMessages );
      }
      const courseCount = this.courseList.length;
      this.logger(`${this.$t('tools.synchronization.msgSynchronizationDone', {courseCount})}`);

    } catch( error ) {
      var message = null;
      if( error.name == 'QuotaExceededError' ) {
        message = `${this.$t('tools.synchronization.msgSynchronizationErrorNoMoreLocalSpace')}`;
      } else if( error.message == 'SynchProcessAbortedError') {
        //once aborted don't do anything
        return;
      } else {
        message = `${this.$t('tools.synchronization.msgSynchronizationErrorGeneral')}`;
      }
      
      this.logger(message);
      this.errorMessageList.push({message, error});

    } finally {
      this.loading = false;
      this.contentIdSelectedForDownloading = 0;
    }

    if( this.errorMessageList.length > 0 ) {
      throw JSON.stringify(this.errorMessageList).replaceAll('"','\'');
    }
  }

  public getFilesCachedAsPercentage(item:ICourseWithResource) {
    if( item.resourceList?.length > 0 ) {
      return Math.floor(item.resourceTotalCached / item.resourceList.length * 100);
    } else {
      return 0;
    }
  }

  public getFilesCachedStatistic(item:ICourseWithResource) {
    return `${item.resourceTotalCached} / ${item.resourceList?.length || 0}`;
  }

  public async init() {
    this.loading = true;
    try { 
      this.courseList = await synchronizationServices.getContentList();
      await synchronizationServices.populateCourseListWithFileInfo(this.courseList, this.logger);
    } finally {
      this.loading = false;
      this.logger(null);
    }
  }

  public logger( msg:string ) {
    const thisVueObjectIsDestroyed = !this.$i18n;
    if( thisVueObjectIsDestroyed ) {
      throw new Error('SynchProcessAbortedError');
    }
    this.message = msg;
  }

  private _checkCourseChanges(course: ICourseWithResource): ICourseWithResource {
    return new Proxy(course, {
      set: (target: any, property: string, value: any) => {
        target[property] = value;
        this.dataTable?.updateItem(target);
        return true;
      }
    });
  }

  private _loadDataInDataTable(): void {

    if (!this.dataTable || !this.dataTable.loadData) {
      return;
    }

    if (!this.courseList || this.courseList.length == 0) {
      this.dataTable.loadData({ items: [], headers: this.courseHeaders });
      return;
    }

    const slots = {
      'item.contentTitle': {
        template: document.querySelector('#item\\.contentTitle').innerHTML,
        templateParams: [
          {key: 'item.contentId'},
          {key: 'item.contentTitle'},
          {key: 'item.errorMessage'},
        ],
        expressions: [
          {
            target: '.downloading', attribute: 'style', booleanAttribute: false,
            expression: (params) => params.item.contentId == params.parent.contentIdSelectedForDownloading,
            applied: 'display: inline; background-color: var(--ed-color-green);', fallback: 'display: none;'
          },
          { 
            target: '.error', attribute: 'style', booleanAttribute: false,
            expression: (params) => !!params.item.errorMessage,
            applied: 'display: inline;', fallback: 'display: none;'
          },
        ],
        customTooltip: [{
            key: 'item.contentTitle',
            show: showCustomTooltip(this, (params: any) => `contentId:${params?.item?.contentId}`),
        }],
      },
      'item.files': {
        template: '{item.files}',
        templateParams: [{
          key: 'item.files',
          value: (params) => params.parent.getFilesCachedStatistic(params.item)
        }],
      },
      'item.resourceCachedPercent': {
        template: '{item.resourceCachedPercent}',
        templateParams: [{
          key: 'item.resourceCachedPercent',
          value: (params) => `${params.parent.getFilesCachedAsPercentage(params.item)} %`
        }],
      },
      parent: this,
    };

    this.dataTable.loadData({
      items: this.courseList,
      headers: this.courseHeaders,
      slots: slots,
    });
  }

  private _onLanguageChanged(lang: string): void {
    this.$nextTick(() => {
      this.dataTable?.setAttribute('lang', lang);
      this._loadDataInDataTable();
    });
  }

  /* Hooks */
  mounted() {
    this.$edEventBus.$on(APP_EVENT_BUS.LANGUAGE_CHANGED, this._onLanguageChanged);
    this.init();
  }
  
  beforeDestroy() {
    this.$edEventBus.$off(APP_EVENT_BUS.LANGUAGE_CHANGED, this._onLanguageChanged);
  }
}

